import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { useSelector, useStore } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { isStrictAdmin } from "@/helpers/UserHelpers";
import { RootState } from "@/store";
import {
  isGoogleAuthorizationUrl,
  useConnectMeetMutation,
  useDisconnectMeetMutation,
  useGetMeetConnectionQuery,
} from "@/services/googleMeet";

/** Check the exact role before mounting any account/email query. */
export default function GoogleMeetSettings() {
  const user = useSelector((state: RootState) => state.auth.user);
  const { generation, blocked } = useSelector(
    (state: RootState) => state.chatSession
  );
  if (!isStrictAdmin(user) || blocked)
    return (
      <Alert variant="danger">
        Ces réglages sont réservés à un administrateur.
      </Alert>
    );
  return (
    <ConnectionSettings
      key={`${user!.id}:${generation}`}
      actorId={user!.id}
      generation={generation}
    />
  );
}
function ConnectionSettings({
  actorId,
  generation,
}: {
  actorId: string;
  generation: number;
}) {
  const store = useStore<RootState>();
  const { search } = useLocation();
  const result = new URLSearchParams(search).get("result");
  const connection = useGetMeetConnectionQuery(
    { actorId },
    { refetchOnMountOrArgChange: true }
  );
  const [connect] = useConnectMeetMutation();
  const [disconnect] = useDisconnectMeetMutation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true),
    locked = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const current = () => {
    const state = store.getState();
    return (
      alive.current &&
      state.auth.user?.id === actorId &&
      isStrictAdmin(state.auth.user) &&
      state.chatSession.generation === generation &&
      !state.chatSession.blocked
    );
  };
  const changeConnection = async (remove: boolean) => {
    if (locked.current || !current()) return;
    locked.current = true;
    setBusy(true);
    setError(null);
    try {
      if (remove) await disconnect({ actorId }).unwrap();
      else {
        const response = await connect({ actorId }).unwrap();
        if (!current()) return;
        if (!isGoogleAuthorizationUrl(response.authorizationUrl))
          throw new Error("Untrusted Google URL");
        window.location.assign(response.authorizationUrl);
      }
    } catch {
      if (current())
        setError(
          "La connexion Google n’a pas pu être mise à jour. Réessayez ou contactez votre administrateur technique."
        );
    } finally {
      locked.current = false;
      if (current()) setBusy(false);
    }
  };
  const denied =
    connection.error &&
    "status" in connection.error &&
    connection.error.status === 403;
  if (denied)
    return (
      <Alert variant="danger">
        Ces réglages sont réservés à un administrateur.
      </Alert>
    );
  const data = connection.currentData;
  return (
    <Card className="p-3 p-md-4">
      <h1 className="h3">Google Meet</h1>
      <p>
        Connectez le compte Google de la société pour créer et partager des
        réunions depuis la messagerie.
      </p>
      <p>
        Formalys utilise l’identité du compte et l’autorisation de créer des salles
        Meet. Les jetons sont conservés côté serveur. Consultez la{" "}
        <a href="/confidentialite.html">politique de confidentialité</a> et les{" "}
        <a href="/conditions-utilisation.html">conditions d’utilisation</a> avant
        de connecter le compte.
      </p>
      {result && (
        <Alert variant="info">
          {result === "connected"
            ? "Connexion Google terminée. L’état du compte est vérifié auprès du serveur."
            : "La connexion Google n’a pas abouti. Vérifiez l’état du compte puis réessayez."}
        </Alert>
      )}
      {(error || connection.isError) && (
        <Alert variant="danger">
          {error ?? "Impossible de charger la connexion Google."}
        </Alert>
      )}
      {connection.isFetching && !data && (
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement</span>
        </Spinner>
      )}
      {data && (
        <>
          <p>
            <strong>
              {data.status === "connected"
                ? "Compte connecté"
                : data.status === "reconnect_required"
                  ? "Reconnexion nécessaire"
                  : "Aucun compte connecté"}
            </strong>
          </p>
          {data.email && <p>{data.email}</p>}
          <div className="d-flex flex-wrap gap-2">
            <Button
              disabled={busy}
              onClick={() => void changeConnection(false)}
            >
              {data.status === "connected"
                ? "Changer de compte Google"
                : data.status === "reconnect_required"
                  ? "Reconnecter Google"
                  : "Connecter Google"}
            </Button>
            {data.status !== "unconfigured" && (
              <Button
                variant="outline-danger"
                disabled={busy}
                onClick={() => void changeConnection(true)}
              >
                Déconnecter
              </Button>
            )}
          </div>
        </>
      )}
      <p className="text-muted mt-3">
        La déconnexion empêche les nouvelles créations. Les liens déjà partagés
        restent dans les conversations.
      </p>
      <Link to="/chat">Retour à la messagerie</Link>
    </Card>
  );
}
